// Dźwięk alertów panelu na Web Audio, nie na `<audio>`.
//
// Powód: iOS ignoruje `HTMLMediaElement.volume` ustawiane z kodu — dzwonek grał tam zawsze
// na 100%. Głośność przez `GainNode` działa wszędzie. Element `<audio>` zostaje tylko jako
// zapasowa droga, gdy Web Audio jest niedostępne albo kontekst nie chce się wznowić.

export type AlertSound = 'new' | 'cancelled';

const SOUND_URL = '/sounds/notification.mp3';

/**
 * Anulowanie gra ten sam plik, ale wolniej (niżej) — osobny plik nigdy nie powstał
 * (w repo leżała tekstowa zaślepka), a recepcja ma usłyszeć różnicę bez patrzenia.
 */
const SOUND_SETTINGS: Record<AlertSound, { volume: number; rate: number }> = {
  new: { volume: 0.3, rate: 1 },
  cancelled: { volume: 0.4, rate: 0.75 },
};

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };
type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

let context: AudioContext | null = null;
let buffer: AudioBuffer | null = null;
let bufferLoading: Promise<AudioBuffer | null> | null = null;
let fallbackAudio: HTMLAudioElement | null = null;
let unlocked = false;
let unlocking = false;

const getContext = (): AudioContext | null => {
  if (context) return context;
  const Ctor = window.AudioContext || (window as WebkitWindow).webkitAudioContext;
  if (!Ctor) return null;

  // Bez tego Safari wycisza Web Audio przełącznikiem „cichy" w iPhonie — a `<audio>`
  // grało mimo niego. `playback` przywraca dotychczasowe zachowanie (Safari 16.4+).
  const { audioSession } = navigator as AudioSessionNavigator;
  if (audioSession) audioSession.type = 'playback';

  context = new Ctor();
  return context;
};

const loadBuffer = (ctx: AudioContext): Promise<AudioBuffer | null> => {
  if (buffer) return Promise.resolve(buffer);
  if (!bufferLoading) {
    bufferLoading = fetch(SOUND_URL)
      .then((response) => response.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .then((decoded) => {
        buffer = decoded;
        return decoded;
      })
      .catch((error) => {
        bufferLoading = null;
        // eslint-disable-next-line no-console
        console.warn('[notifications] nie udało się wczytać dźwięku', error);
        return null;
      });
  }
  return bufferLoading;
};

const playFallback = (sound: AlertSound) => {
  if (!fallbackAudio) {
    fallbackAudio = new Audio(SOUND_URL);
    fallbackAudio.preload = 'auto';
  }
  fallbackAudio.volume = SOUND_SETTINGS[sound].volume;
  fallbackAudio.playbackRate = SOUND_SETTINGS[sound].rate;
  try {
    fallbackAudio.currentTime = 0;
  } catch {
    // Ścieżka jeszcze się nie załadowała — `play()` i tak zacznie od zera.
  }
  fallbackAudio.play().catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('[notifications] nie udało się odtworzyć dźwięku', error);
  });
};

/**
 * Odblokowanie dźwięku gestem użytkownika.
 *
 * Przeglądarki wpuszczają dźwięk dopiero po interakcji z domeną, a recepcja potrafi nie
 * dotykać panelu godzinami. Wznawiamy więc kontekst przy geście i puszczamy jedną
 * pustą ramkę (iOS odblokowuje dopiero realnym odtworzeniem) — nic nie słychać.
 * Flaga ustawia się dopiero, gdy kontekst naprawdę ruszył; inaczej czekamy na kolejny gest.
 */
export const unlockAlertSound = () => {
  if (unlocked || unlocking) return;
  const ctx = getContext();
  if (!ctx) return;
  unlocking = true;

  const silent = ctx.createBufferSource();
  silent.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
  silent.connect(ctx.destination);
  silent.start(0);

  ctx.resume()
    .then(() => {
      unlocked = ctx.state === 'running';
      return loadBuffer(ctx);
    })
    .catch(() => undefined)
    .finally(() => {
      unlocking = false;
    });
};

export const isAlertSoundUnlocked = () => unlocked;

export const playAlertSound = async (sound: AlertSound) => {
  const ctx = getContext();
  if (!ctx) {
    playFallback(sound);
    return;
  }

  // iOS usypia kontekst po przerwaniu (połączenie, inna aplikacja z dźwiękiem).
  if (ctx.state !== 'running') await ctx.resume().catch(() => undefined);
  const decoded = await loadBuffer(ctx);
  if (ctx.state !== 'running' || !decoded) {
    playFallback(sound);
    return;
  }

  const { volume, rate } = SOUND_SETTINGS[sound];
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = decoded;
  source.playbackRate.value = rate;
  gain.gain.value = volume;
  source.connect(gain).connect(ctx.destination);
  source.start();
};
